import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  AuthService,
  HttpService,
  TranslatePipe,
  TranslationService,
} from '@nebula-shared/data-access';
import { finalize } from 'rxjs/operators';

/** Estados de la pantalla de cambio de contraseña vencida. */
type CambiarEstado = 'formulario' | 'enviando' | 'listo';

/**
 * Pantalla de cambio de contraseña OBLIGATORIO para un usuario ya autenticado cuya contraseña
 * venció (login devolvió {@code mustChangePassword=true}). Define la nueva contraseña respetando la
 * política internacional, la envía a SimappeAdmin ({@code update-password}) con el token de la sesión
 * y, al confirmar, cierra la sesión para que el usuario vuelva a entrar con la nueva contraseña.
 */
@Component({
  selector: 'nebula-cambiar-clave',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslatePipe,
  ],
  templateUrl: './cambiar-clave.component.html',
  styleUrls: ['./cambiar-clave.component.scss'],
})
export class CambiarClaveComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly http = inject(HttpService);
  private readonly auth = inject(AuthService);
  private readonly translation = inject(TranslationService);

  /** Endpoint autenticado de cambio de contraseña en SimappeAdmin. */
  private static readonly UPDATE_PASSWORD = '/simappe-admin/api/v1/user-identity/update-password';

  /** Estado de la pantalla. */
  readonly estado = signal<CambiarEstado>('formulario');

  /** Mensaje de error a mostrar (si aplica). */
  readonly error = signal<string>('');

  /** Alterna la visibilidad de cada campo de contraseña. */
  hideNew = true;
  hideConfirm = true;

  /** Formulario de definición de la nueva contraseña (misma política que la activación). */
  readonly form: FormGroup = this.fb.group(
    {
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/),
        ],
      ],
      confirmar: ['', [Validators.required]],
    },
    { validators: CambiarClaveComponent.contrasenasCoinciden },
  );

  /** Validador de coincidencia de contraseñas. */
  private static contrasenasCoinciden(group: AbstractControl): ValidationErrors | null {
    const pass = group.get('password')?.value;
    const conf = group.get('confirmar')?.value;
    return pass && conf && pass !== conf ? { noCoinciden: true } : null;
  }

  /**
   * Envía la nueva contraseña. Al terminar bien, limpia la marca de cambio pendiente y cierra la
   * sesión para forzar un nuevo login con la contraseña nueva.
   */
  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const userId = this.auth.getCurrentUser()?.id;
    if (!userId) {
      this.router.navigate(['/login']);
      return;
    }

    this.error.set('');
    this.estado.set('enviando');
    this.http
      .put<void>(`${CambiarClaveComponent.UPDATE_PASSWORD}?userId=${userId}`, {
        param1: '',
        param2: this.form.value.password,
      })
      .pipe(finalize(() => this.form.enable()))
      .subscribe({
        next: () => {
          this.estado.set('listo');
          this.auth.clearMustChangePassword();
          setTimeout(() => this.auth.logout(), 2500);
        },
        error: (e) => {
          this.estado.set('formulario');
          this.error.set(
            e?.error?.response?.content ?? this.translation.translate('CHANGE_PWD.ERROR'),
          );
        },
      });
  }
}
